import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMassager,
  getMassager,
  getMassagers,
  updateMassager,
} from '@/services/admin/massagers';
import { useDownloadMassagersCsv } from '@/hooks/admin/useDownloadMassagersCsv';
import type {
  CreateMassagerPayload,
  UpdateMassagerPayload,
  MassagerListParams,
} from '@/services/admin/massagers';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import type { AxiosError } from 'axios';

export type {
  CreateMassagerPayload,
  UpdateMassagerPayload,
  MassagerListParams,
} from '@/services/admin/massagers';

export const useAdminCreateMassager = () => {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const { mutateAsync: createMassagerMutation, isPending: isLoading } = useMutation({
    mutationFn: ({ site, data }: { site: string; data: CreateMassagerPayload }) =>
      createMassager(site, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinics'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinics', variables.data.clinic_id] });
      queryClient.invalidateQueries({
        queryKey: ['clinic-detail', String(variables.data.clinic_id), variables.site],
      });
      // Invalidate clinic location detail so 施術者 dropdown on create manager page shows new massager
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinic-locations'] });
      if (response?.success) {
        toast.success('更新しました。');
      } else {
        toast.error(response?.message ?? '登録に失敗しました。');
      }
    },
    onError: error => {
      const err = error as AxiosError<any>;
      const data = err.response?.data as any;
      const clinicIdErrors: string[] | undefined = data?.errors?.clinic_id;
      const clinicNotFoundMsg =
        '指定された治療院は存在しません。他の治療院を選んでください。';
      const message = (data?.message ?? '').toString().trim();

      if (
        (Array.isArray(clinicIdErrors) && clinicIdErrors.includes(clinicNotFoundMsg)) ||
        message === clinicNotFoundMsg
      ) {
        toast.error(clinicNotFoundMsg);
      } else {
        toast.error(getApiErrorMessage(error, '登録に失敗しました。'));
      }
    },
  });

  return { createMassagerMutation, isLoading };
};

/**
 * Fetch a single massager for edit form (admin portal).
 */
export const useAdminMassager = (site: string | null, massagerId: number | null) => {
  const toast = useAppToast();
  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: ['admin', 'massagers', site ?? '', massagerId ?? 0],
    queryFn: () => getMassager(site!, massagerId!),
    enabled: !!site && !!massagerId && massagerId > 0,
  });
  useEffect(() => {
    if (isError && error) {
      const anyErr = error as any;
      const status = anyErr?.response?.status;
      // 404 は画面側で NotFound テンプレートを表示するのでトーストは出さない
      if (status === 404) return;
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);
  return { data, isLoading, isError, isPending, refetch, error };
};

export const useAdminUpdateMassager = () => {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const { mutateAsync: updateMassagerMutation, isPending: isLoading } = useMutation({
    mutationFn: ({ site, id, data }: { site: string; id: number; data: UpdateMassagerPayload }) =>
      updateMassager(site, id, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinics'] });
      const clinicId = result?.data?.clinic_id;
      if (clinicId != null) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'clinics', clinicId] });
        queryClient.invalidateQueries({
          queryKey: ['clinic-detail', String(clinicId), variables.site],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['admin', 'massagers', variables.site, variables.id],
      });
      // Invalidate clinic location detail so 施術者 dropdown on create manager page shows updated massager
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinic-locations'] });
      if (result?.success) {
        toast.success(result.message ?? 'はり・きゅう師を更新しました。');
      } else {
        toast.error(result?.message ?? '更新に失敗しました。');
      }
    },
    onError: error => {
      const err = error as AxiosError<any>;
      if (err.response?.status === 422 && err.response?.data?.errors) {
        return;
      }
      const msg = getApiErrorMessage(error, '');
      toast.error(msg || '更新に失敗しました。');
    },
  });

  return { updateMassagerMutation, isLoading };
};

/**
 * Fetch admin massager list (RA2 はり・きゅう師CSV list).
 * Uses getApiErrorMessage + toast on fetch error per project convention.
 */
export const useAdminMassagersList = (site: string | null, params: MassagerListParams) => {
  const toast = useAppToast();
  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: ['admin', 'massagers', site ?? '', params],
    queryFn: () => getMassagers(site!, params),
    enabled: !!site,
    /** Override global staleTime (1h): list must refetch on search / invalidate. */
    staleTime: 0,
  });
  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);
  return { data, isLoading, isError, isPending, refetch, error };
};

/**
 * Download admin massagers CSV (toast handled inside useDownloadMassagersCsv).
 */
export const useAdminMassagersCsvDownload = () => useDownloadMassagersCsv();
